import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import DataTable from "@/components/admin/DataTable";
import EmptyState from "@/components/admin/EmptyState";
import StatusBadge from "@/components/admin/StatusBadge";
import ConfirmActionDialog from "@/components/admin/ConfirmActionDialog";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "pending_review", label: "Pending" },
  { value: "info_requested", label: "Info requested" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export default function AdminPainterApplications() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState("pending_review");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [actionDialog, setActionDialog] = useState(null); // { kind, reason }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/painter-applications", {
        params: { status: status || undefined, page, page_size: pageSize },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setSelected(row);
    setDetail(null);
    try {
      const r = await api.get(`/admin/painter-applications/${row.id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runAction = async (kind, reason) => {
    if (!selected) return;
    const url =
      kind === "approve" ? `/admin/painter-applications/${selected.id}/approve` :
      kind === "reject" ? `/admin/painter-applications/${selected.id}/reject` :
      `/admin/painter-applications/${selected.id}/info-request`;
    try {
      await api.post(url, kind === "approve" ? {} : { reason });
      toast.success(
        kind === "approve" ? "Application approved" :
        kind === "reject" ? "Application rejected" :
        "Applicant notified — info requested"
      );
      setActionDialog(null);
      setSelected(null);
      setDetail(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const columns = [
    { key: "business_name", label: "Business" },
    { key: "applicant_name", label: "Owner" },
    {
      key: "hq",
      label: "Location",
      render: (r) => `${r.hq_state} ${r.hq_postcode}`,
    },
    { key: "abn_digits", label: "ABN" },
    {
      key: "status",
      label: "Status",
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      key: "created_at",
      label: "Submitted",
      render: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "",
    },
  ];

  return (
    <div data-testid="admin-painter-applications">
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <div className="overline mb-1">Status</div>
          <select
            data-testid="filter-status"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="border border-brand-linen p-2 text-sm"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s.value || "all"} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="ml-auto text-sm text-brand-muted" data-testid="count">
          {total} application{total === 1 ? "" : "s"}
        </div>
      </div>

      <DataTable
        testId="painter-applications-table"
        columns={columns}
        rows={items}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={openDetail}
        loading={loading}
        empty={<EmptyState testId="admin-painter-applications-empty" title="No applications" caption="Painter applications will appear here as they're submitted." />}
      />

      {selected && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-6 overflow-y-auto"
          onClick={() => { setSelected(null); setDetail(null); }}
          data-testid="application-detail-drawer"
        >
          <div
            className="bg-white max-w-3xl w-full p-8 my-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="overline">Painter application</div>
                <h2 className="font-serif text-2xl text-brand-ink mt-1">{selected.business_name}</h2>
                <div className="text-sm text-brand-muted mt-1">
                  {selected.applicant_name} · {selected.applicant_email}
                </div>
              </div>
              <StatusBadge status={selected.status} />
            </div>

            {!detail && <div className="text-sm text-brand-muted">Loading detail…</div>}
            {detail && (
              <div className="space-y-4 text-sm">
                <Row label="ABN" value={detail.abn_digits} />
                <Row label="HQ" value={`${detail.submitted_payload?.hq_suburb}, ${detail.hq_state} ${detail.hq_postcode}`} />
                <Row label="Phone" value={detail.submitted_payload?.contact_phone} />
                <Row label="Website" value={detail.submitted_payload?.website_url || "—"} />
                <Row label="Service scope" value={(detail.submitted_payload?.service_scope || []).join(", ") || "—"} />
                <Row label="Service surfaces" value={(detail.submitted_payload?.service_surfaces || []).join(", ") || "—"} />
                <Row label="Property types" value={(detail.submitted_payload?.property_types || []).join(", ") || "—"} />
                <Row label="Service postcodes" value={(detail.submitted_payload?.service_postcodes || []).join(", ") || "—"} />
                <Row label="Licence ref" value={detail.submitted_payload?.licence_reference || "—"} />
                <Row label="Insurance ref" value={detail.submitted_payload?.insurance_reference || "—"} />
                {detail.review_history?.length > 0 && (
                  <div className="border-t border-brand-linen pt-4 mt-4">
                    <div className="overline mb-2">Review history</div>
                    <ul className="space-y-2">
                      {detail.review_history.map((h, i) => (
                        <li key={i} className="text-xs text-brand-muted">
                          {new Date(h.at).toLocaleString()} · {h.action}
                          {h.reason ? ` — ${h.reason}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {detail && detail.status === "approved" && detail.painter_id && (
              <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-brand-linen">
                <Link
                  to={`/admin/painters/${detail.painter_id}`}
                  data-testid="admin-view-painter-detail-link"
                  className="px-5 py-2.5 bg-brand-navy text-white text-sm hover:opacity-90 inline-flex items-center gap-2 rounded-full"
                >
                  View painter detail →
                </Link>
                <p className="text-xs text-brand-muted self-center">
                  Open the full Premium-CRM detail page for this approved painter.
                </p>
              </div>
            )}

            {detail && detail.status !== "approved" && detail.status !== "rejected" && (
              <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-brand-linen">
                <button
                  data-testid="action-approve"
                  onClick={() => setActionDialog({ kind: "approve" })}
                  className="px-5 py-2.5 bg-brand-navy text-white text-sm hover:opacity-90"
                >
                  Approve
                </button>
                <button
                  data-testid="action-info-request"
                  onClick={() => setActionDialog({ kind: "info-request" })}
                  className="px-5 py-2.5 border border-brand-linen text-sm hover:bg-brand-linen/30"
                >
                  Request info
                </button>
                <button
                  data-testid="action-reject"
                  onClick={() => setActionDialog({ kind: "reject" })}
                  className="px-5 py-2.5 border border-red-300 text-red-700 text-sm hover:bg-red-50"
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {actionDialog && (
        <ConfirmActionDialog
          open={true}
          onClose={() => setActionDialog(null)}
          title={
            actionDialog.kind === "approve" ? "Approve application?" :
            actionDialog.kind === "reject"  ? "Reject application?" :
            "Request more information?"
          }
          description={
            actionDialog.kind === "approve"
              ? "The applicant will be activated, sent a welcome email with their dashboard link, and listed as a verified painter."
              : "The applicant will receive an email with the reason you provide."
          }
          reasonRequired={actionDialog.kind !== "approve"}
          reasonMinLength={4}
          reasonLabel={actionDialog.kind === "approve" ? "" : "Reason for the applicant"}
          confirmLabel={
            actionDialog.kind === "approve" ? "Approve" :
            actionDialog.kind === "reject"  ? "Reject" :
            "Send request"
          }
          destructive={actionDialog.kind === "reject"}
          onConfirm={(reason) => runAction(actionDialog.kind, reason)}
          testId={`confirm-${actionDialog.kind}`}
        />
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-4">
      <div className="overline w-40 shrink-0">{label}</div>
      <div className="text-brand-ink">{value}</div>
    </div>
  );
}
